import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import config from "../config";

const OutOfStockProducts = () => {
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingAll, setUpdatingAll] = useState(false);
  const [removingDiscountId, setRemovingDiscountId] = useState(null);

  // Get the admin token and prepare authorization headers.
  const adminHeaders = useCallback(() => {
    const token = localStorage.getItem("admintoken");

    if (!token) {
      throw new Error("Admin session missing. Please log in again.");
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  // Handle API errors consistently.
  const getErrorMessage = (err, fallback) => {
    if (err.response?.status === 401) {
      return "Your admin session is invalid or expired. Please log in again.";
    }

    if (err.response?.status === 403) {
      return "You do not have permission to perform this action.";
    }

    return (
      err.response?.data?.message ||
      err.message ||
      fallback
    );
  };

  // Fetch sold-out products.
  const fetchOutOfStockProducts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const headers = adminHeaders();

      console.log(
        "Fetching out-of-stock products:",
        `${config}/products/out-of-stock`
      );
      console.log("Admin token exists:", true);

      const response = await axios.get(
        `${config}/products/out-of-stock`,
        headers
      );

      if (!Array.isArray(response.data)) {
        throw new Error(
          "Unexpected API response. Expected an array of products."
        );
      }

      setOutOfStockProducts(response.data);
    } catch (err) {
      console.error("Out-of-stock products API error:", {
        status: err.response?.status,
        message: err.response?.data?.message,
        data: err.response?.data,
      });

      setError(
        getErrorMessage(
          err,
          "Unable to load sold-out products. Please try again."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [adminHeaders]);

  useEffect(() => {
    fetchOutOfStockProducts();
  }, [fetchOutOfStockProducts]);

  // Activate or deactivate one product.
  const handleAvailabilityChange = async (product) => {
    const isActive = product.isActive === false;

    setUpdatingId(product._id);
    setError("");
    setMessage("");

    try {
      const response = await axios.put(
        `${config}/admin/products/${product._id}/availability`,
        { isActive },
        adminHeaders()
      );

      const updatedProduct = response.data?.product || response.data;

      setOutOfStockProducts((products) =>
        products.map((item) =>
          item._id === product._id
            ? { ...item, ...updatedProduct, isActive }
            : item
        )
      );

      setMessage(
        `${product.name} is now ${
          isActive ? "active" : "inactive"
        }.`
      );
    } catch (err) {
      console.error("Product availability update error:", {
        status: err.response?.status,
        data: err.response?.data,
      });

      setError(
        getErrorMessage(
          err,
          `Unable to update ${product.name}.`
        )
      );
    } finally {
      setUpdatingId(null);
    }
  };

  // Activate or deactivate all sold-out products.
  const handleBulkAvailabilityChange = async (isActive) => {
    setUpdatingAll(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.put(
        `${config}/admin/products/out-of-stock/availability`,
        { isActive },
        adminHeaders()
      );

      // Refetch so the UI reflects the actual server-side filter.
      await fetchOutOfStockProducts();

      setMessage(
        response.data?.message ||
          `All sold-out products are now ${
            isActive ? "active" : "inactive"
          }.`
      );
    } catch (err) {
      console.error("Bulk availability update error:", {
        status: err.response?.status,
        data: err.response?.data,
      });

      setError(
        getErrorMessage(
          err,
          "Unable to update product availability."
        )
      );
    } finally {
      setUpdatingAll(false);
    }
  };

  // Remove a product discount.
  const handleRemoveDiscount = async (productId) => {
    setRemovingDiscountId(productId);
    setError("");
    setMessage("");

    try {
      await axios.put(
        `${config}/products/remove-discount/${productId}`,
        {},
        adminHeaders()
      );

      await fetchOutOfStockProducts();

      setMessage("Discount removed successfully.");
    } catch (err) {
      console.error("Remove discount API error:", {
        status: err.response?.status,
        data: err.response?.data,
      });

      setError(
        getErrorMessage(
          err,
          "Unable to remove discount."
        )
      );
    } finally {
      setRemovingDiscountId(null);
    }
  };

  const hasInactiveProducts = outOfStockProducts.some(
    (product) => product.isActive === false
  );

  const hasActiveProducts = outOfStockProducts.some(
    (product) => product.isActive !== false
  );

  const isUpdating =
    updatingAll ||
    updatingId !== null ||
    removingDiscountId !== null;

  const buttonBase =
    "inline-flex items-center justify-center rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className="min-h-screen bg-gray-50/70 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-gray-400">
              <span>Inventory</span>
              <span>/</span>
              <span className="text-gray-600">
                Stock management
              </span>
            </div>

            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
              Sold-out products
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Manage product visibility and discounts in one place.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleBulkAvailabilityChange(true)}
              disabled={
                loading ||
                isUpdating ||
                !hasInactiveProducts
              }
              className={`${buttonBase} border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50`}
            >
              {updatingAll ? "Updating..." : "Activate all"}
            </button>

            <button
              type="button"
              onClick={() => handleBulkAvailabilityChange(false)}
              disabled={
                loading ||
                isUpdating ||
                !hasActiveProducts
              }
              className={`${buttonBase} bg-gray-900 text-white hover:bg-gray-800`}
            >
              {updatingAll ? "Updating..." : "Deactivate all"}
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-sm text-gray-500">
              Sold-out products
            </p>

            <div className="mt-3 flex items-end justify-between">
              <span className="text-3xl font-semibold tracking-tight text-gray-900">
                {loading ? "—" : outOfStockProducts.length}
              </span>

              <span className="rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-600">
                Out of stock
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-sm text-gray-500">
              Visible on website
            </p>

            <div className="mt-3 flex items-end justify-between">
              <span className="text-3xl font-semibold tracking-tight text-gray-900">
                {loading
                  ? "—"
                  : outOfStockProducts.filter(
                      (product) => product.isActive !== false
                    ).length}
              </span>

              <span className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                Active
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-sm text-gray-500">
              Hidden from website
            </p>

            <div className="mt-3 flex items-end justify-between">
              <span className="text-3xl font-semibold tracking-tight text-gray-900">
                {loading
                  ? "—"
                  : outOfStockProducts.filter(
                      (product) => product.isActive === false
                    ).length}
              </span>

              <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                Inactive
              </span>
            </div>
          </div>
        </div>

        {/* Error notification */}
        {error && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span>{error}</span>

            {!loading && (
              <button
                type="button"
                onClick={fetchOutOfStockProducts}
                className="font-semibold underline underline-offset-2"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {/* Success notification */}
        {message && (
          <div
            role="status"
            className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100">
              ✓
            </span>

            {message}
          </div>
        )}

        {/* Product table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-100/60">
          <div className="flex flex-col gap-1 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Product inventory
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Products currently listed as out of stock.
              </p>
            </div>

            {!loading && (
              <span className="mt-2 w-fit rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 sm:mt-0">
                {outOfStockProducts.length} products
              </span>
            )}
          </div>

          {loading ? (
            <div className="space-y-4 p-6">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="h-12 animate-pulse rounded-lg bg-gray-100"
                />
              ))}
            </div>
          ) : outOfStockProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  className="h-7 w-7"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m5 12 4 4L19 6"
                  />
                </svg>
              </div>

              <h3 className="mt-4 text-base font-semibold text-gray-900">
                All caught up
              </h3>

              <p className="mt-1 max-w-sm text-sm text-gray-500">
                There are currently no sold-out products to manage.
              </p>

              <button
                type="button"
                onClick={fetchOutOfStockProducts}
                className="mt-4 text-sm font-medium text-gray-700 underline underline-offset-4"
              >
                Refresh inventory
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-gray-50/80 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-5 py-4 font-medium">
                      Product
                    </th>
                    <th className="px-5 py-4 font-medium">
                      Category
                    </th>
                    <th className="px-5 py-4 font-medium">
                      Price
                    </th>
                    <th className="px-5 py-4 font-medium">
                      Stock
                    </th>
                    <th className="px-5 py-4 font-medium">
                      Discount
                    </th>
                    <th className="px-5 py-4 font-medium">
                      Website status
                    </th>
                    <th className="px-5 py-4 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {outOfStockProducts.map((product) => {
                    const isActive = product.isActive !== false;

                    return (
                      <tr
                        key={product._id}
                        className="transition-colors hover:bg-gray-50/70"
                      >
                        <td className="px-5 py-4">
                          <div className="font-medium text-gray-900">
                            {product.name}
                          </div>

                          <div className="mt-1 text-xs text-gray-400">
                            ID: {product._id}
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex max-w-48 flex-wrap gap-1.5">
                            {product.category?.length ? (
                              product.category.map((category) => (
                                <span
                                  key={category}
                                  className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600"
                                >
                                  {category}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400">
                                —
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 font-medium text-gray-900">
                          ₹
                          {Number(
                            product.price || 0
                          ).toLocaleString("en-IN")}
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-2 font-semibold text-red-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                            {product.stock ?? "—"}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          {product.hasOffer ? (
                            <div>
                              <span className="font-medium text-gray-900">
                                ₹
                                {Number(
                                  product.offerPrice || 0
                                ).toLocaleString("en-IN")}
                              </span>

                              <div className="mt-1 text-xs text-emerald-600">
                                Offer applied
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">
                              No discount
                            </span>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                              isActive
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                isActive
                                  ? "bg-emerald-500"
                                  : "bg-gray-400"
                              }`}
                            />

                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              aria-pressed={isActive}
                              disabled={isUpdating}
                              onClick={() =>
                                handleAvailabilityChange(product)
                              }
                              className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                                isActive
                                  ? "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-100"
                                  : "border-gray-900 bg-gray-900 text-white hover:bg-gray-800"
                              }`}
                            >
                              {updatingId === product._id
                                ? "Saving..."
                                : isActive
                                  ? "Deactivate"
                                  : "Activate"}
                            </button>

                            {product.hasOffer && (
                              <button
                                type="button"
                                disabled={isUpdating}
                                onClick={() =>
                                  handleRemoveDiscount(product._id)
                                }
                                className="rounded-lg border border-red-100 px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {removingDiscountId === product._id
                                  ? "Removing..."
                                  : "Remove offer"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {!loading && outOfStockProducts.length > 0 && (
            <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50/50 px-5 py-3 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                Showing {outOfStockProducts.length} sold-out products
              </span>

              <button
                type="button"
                onClick={fetchOutOfStockProducts}
                disabled={isUpdating}
                className="w-fit font-medium text-gray-700 underline-offset-4 hover:underline disabled:opacity-40"
              >
                Refresh inventory
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OutOfStockProducts;
